import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import DatePicker from "react-datepicker";
import Select from "react-select";
import Table from "./table";
import Pagination from "./Pagination";
import DateInput from "./DateInput";
import { NavErrorBanner, NavLoading } from "./status";
import { navHistoryColumns } from "./list_of_columns";
import { groupDigits } from "./cell_formatter";
import {
  DEFAULT_PAGE_SIZE,
  NAV_PATHS,
  PAGE_SIZE_OPTIONS,
  describeError,
  toApiDate,
  useFundOptions,
  useNavRequest
} from "./api";
import Images from "../../utils/styles/images";
import messages from "./messages";
import Icon from "../../components/Icon";

/** Sum of the column widths in navHistoryColumns, so fourteen columns scroll instead of squashing. */
const TABLE_MIN_WIDTH = 2140;

/**
 * type_nav values the sync writes into source_type_nav. It is a diagnostic column, which is exactly
 * why an operator wants to filter on it: "show me everything type_nav 2 produced".
 */
const SOURCE_TYPE_NAV_VALUES = [2, 3, 4, 5, 6];

/** The two jobs that write NAV rows: the cronjob and a manual CLI run. */
const SYNC_BY_VALUES = ["cronjob", "manual"];

const EMPTY_FILTERS = {
  fundNo: undefined,
  fundCode: "",
  dateFrom: undefined,
  dateTo: undefined,
  sourceTypeNav: undefined,
  syncBy: undefined
};

function NavHistoryTab() {
  const intl = useIntl();
  const requestNav = useNavRequest();
  const { fundOptions, loadingFunds } = useFundOptions();

  const columns = useMemo(() => navHistoryColumns(intl), [intl]);

  // Draft filter state - what the operator is typing. Nothing is sent until Search is pressed.
  const [fundOption, setFundOption] = useState(null);
  const [fundCode, setFundCode] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [sourceTypeOption, setSourceTypeOption] = useState(null);
  const [syncByOption, setSyncByOption] = useState(null);

  // Applied filter state - what the current table actually shows.
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [pageIndex, setPageIndex] = useState(0);
  const [sortBy, setSortBy] = useState("fund_no");
  const [sortOrder, setSortOrder] = useState("asc");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [requesting, setRequesting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Guards against a slow earlier request overwriting the results of a newer one, and against
  // setting state after the tab has been left.
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchNavHistory = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setRequesting(true);
    setErrorMessage(null);

    try {
      const result = await requestNav(NAV_PATHS.history, {
        page: pageIndex + 1,
        per_page: perPage,
        fund_no: appliedFilters.fundNo,
        fund_code: appliedFilters.fundCode,
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        source_type_nav: appliedFilters.sourceTypeNav,
        sync_by: appliedFilters.syncBy,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      if (!mountedRef.current || requestIdRef.current !== requestId || result.signedOut) {
        return;
      }
      setRows(result.data);
      setTotal(Number(result.total) || 0);
    } catch (error) {
      if (!mountedRef.current || requestIdRef.current !== requestId) {
        return;
      }
      setRows([]);
      setTotal(0);
      setErrorMessage(describeError(error, intl, messages));
    } finally {
      if (mountedRef.current && requestIdRef.current === requestId) {
        setRequesting(false);
      }
    }
  }, [requestNav, intl, appliedFilters, perPage, pageIndex, sortBy, sortOrder]);

  useEffect(() => {
    fetchNavHistory();
  }, [fetchNavHistory]);

  const applyFilters = () => {
    setPageIndex(0);
    setAppliedFilters({
      fundNo: fundOption ? fundOption.value : undefined,
      fundCode: fundCode.trim(),
      dateFrom: toApiDate(dateFrom),
      dateTo: toApiDate(dateTo),
      sourceTypeNav: sourceTypeOption ? sourceTypeOption.value : undefined,
      syncBy: syncByOption ? syncByOption.value : undefined
    });
  };

  const clearFilters = () => {
    setFundOption(null);
    setFundCode("");
    setDateFrom(null);
    setDateTo(null);
    setSourceTypeOption(null);
    setSyncByOption(null);
    setPageIndex(0);
    setAppliedFilters(EMPTY_FILTERS);
  };

  // Sorting is done by the server: the sort key goes back to the API and the page is refetched.
  // Re-ordering the rows already on screen would sort one page of a 128,466-row table.
  const handleHeaderSort = sortKey => {
    if (sortBy === sortKey) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(sortKey);
      setSortOrder("asc");
    }
    setPageIndex(0);
  };

  const sourceTypeOptions = SOURCE_TYPE_NAV_VALUES.map(value => ({
    value,
    label: String(value)
  }));
  const syncByOptions = SYNC_BY_VALUES.map(value => ({ value, label: value }));
  const perPageOptions = PAGE_SIZE_OPTIONS.map(value => ({
    value,
    label: String(value)
  }));

  const pageCount = perPage > 0 ? Math.ceil(total / perPage) : 0;

  return (
    <React.Fragment>
      {requesting ? (
        <NavLoading text={intl.formatMessage(messages.loadingNavHistory)} />
      ) : null}

      <NavErrorBanner message={errorMessage} onRetry={fetchNavHistory} />

      <div className="nav-filter-bar">
        <div className="nav-filter-field nav-filter-field-wide">
          <span className="nav-filter-label">
            {intl.formatMessage(messages.filterFund)}
          </span>
          <Select
            options={fundOptions}
            value={fundOption}
            onChange={option => setFundOption(option)}
            isClearable
            isLoading={loadingFunds}
            maxMenuHeight={220}
            className="basic-single"
            classNamePrefix="select"
            placeholder={intl.formatMessage(
              loadingFunds ? messages.filterFundLoading : messages.filterFundPlaceholder
            )}
          />
        </div>

        <div className="nav-filter-field">
          <span className="nav-filter-label">
            {intl.formatMessage(messages.filterFundCode)}
          </span>
          <div className="nav-search">
            <img
              src={Images.search.search}
              alt="search-icon"
              className="nav-list-begin-search"
              onClick={() => applyFilters()}
            />
            <input
              placeholder={intl.formatMessage(messages.filterFundCodePlaceholder)}
              className="nav-search-bar"
              value={fundCode}
              onChange={event => setFundCode(event.target.value)}
              onKeyDown={event => {
                if (event.keyCode === 13) {
                  applyFilters();
                }
              }}
            />
            {fundCode && fundCode.length > 0 ? (
              <img
                src={Images.closeButton.close}
                alt="cancel-search"
                className="nav-list-cancel-search"
                onClick={() => setFundCode("")}
              />
            ) : null}
          </div>
        </div>

        <div className="nav-filter-field nav-filter-field-date">
          <span className="nav-filter-label">
            {intl.formatMessage(messages.filterNavDate)}
          </span>
          <div className="nav-date-range">
            <DatePicker
              selected={dateFrom}
              onChange={date => setDateFrom(date)}
              selectsStart
              startDate={dateFrom}
              endDate={dateTo}
              dateFormat="dd/MM/yy"
              customInput={<DateInput />}
            />
            <p className="nav-date-range-separator">-</p>
            <DatePicker
              selected={dateTo}
              onChange={date => setDateTo(date)}
              selectsEnd
              startDate={dateFrom}
              endDate={dateTo}
              minDate={dateFrom}
              dateFormat="dd/MM/yy"
              customInput={<DateInput />}
            />
          </div>
        </div>

        <div className="nav-filter-field nav-filter-field-narrow">
          <span className="nav-filter-label">
            {intl.formatMessage(messages.filterSourceTypeNav)}
          </span>
          <Select
            options={sourceTypeOptions}
            value={sourceTypeOption}
            onChange={option => setSourceTypeOption(option)}
            isClearable
            maxMenuHeight={220}
            className="basic-single"
            classNamePrefix="select"
            placeholder={intl.formatMessage(messages.filterAnyPlaceholder)}
          />
        </div>

        <div className="nav-filter-field nav-filter-field-narrow">
          <span className="nav-filter-label">
            {intl.formatMessage(messages.filterSyncBy)}
          </span>
          <Select
            options={syncByOptions}
            value={syncByOption}
            onChange={option => setSyncByOption(option)}
            isClearable
            maxMenuHeight={220}
            className="basic-single"
            classNamePrefix="select"
            placeholder={intl.formatMessage(messages.filterAnyPlaceholder)}
          />
        </div>

        <div className="nav-filter-field nav-filter-field-narrow">
          <span className="nav-filter-label">
            {intl.formatMessage(messages.filterRowsPerPage)}
          </span>
          <Select
            options={perPageOptions}
            value={perPageOptions.find(option => option.value === perPage)}
            onChange={option => {
              setPerPage(option.value);
              setPageIndex(0);
            }}
            maxMenuHeight={220}
            className="basic-single"
            classNamePrefix="select"
          />
        </div>

        <div className="nav-filter-actions">
          <button className="nav-search-button" onClick={() => applyFilters()}>
            <Icon name="search" size={18} />
            {intl.formatMessage(messages.buttonSearch)}
          </button>
          <button className="nav-clear-button" onClick={() => clearFilters()}>
            <Icon name="close" size={18} />
            {intl.formatMessage(messages.buttonClear)}
          </button>
        </div>
      </div>

      <p className="nav-total-rows">
        {intl.formatMessage(messages.totalRows, { total: groupDigits(total) })}
      </p>

      <Table
        columns={columns}
        data={rows}
        handleActionHeaderClick={handleHeaderSort}
        sortBy={sortBy}
        sortOrder={sortOrder}
        errorMessage={errorMessage}
        onRetry={fetchNavHistory}
        tableMinWidth={TABLE_MIN_WIDTH}
      />

      {pageCount > 1 ? (
        <Pagination nPageIndex={pageIndex} total={pageCount} gotoPage={setPageIndex} />
      ) : null}
    </React.Fragment>
  );
}

export default NavHistoryTab;
